/**
 * Patriot tools — support-only views over the Patriot replica. The pages
 * themselves live in the app; this is the data they read and the rules for
 * reading it.
 */
export {
  useTenantActivations,
  useTenantActivation,
  useFleetActivations,
  useFleetActivation,
  useTenantSignals,
  ACTIVATIONS_PAGE_SIZE,
  SIGNALS_PAGE_SIZE,
  type UseTenantActivationsOptions,
  type UseFleetActivationsOptions,
  type UseActivationsResult,
  type UseFleetListResult,
  type UseActivationResult,
  type UseTenantSignalsOptions,
  type UseSignalsResult,
} from "./hooks";
export {
  useFleetClients,
  useFleetClient,
  CLIENTS_PAGE_SIZE,
  type FleetClientFilters,
  type UseClientsResult,
  type UseClientResult,
} from "./clients";
export type {
  ClientActionPlan,
  ClientActionPlanInstructions,
  ClientActionPlanOverride,
  ClientAddress,
  ClientDetail,
  ClientEventType,
  ClientTemplate,
  ClientGroupingSummary,
  ClientSummary,
} from "../../client-messages/clients";
export {
  useFleetOperatorNotes,
  OPERATOR_NOTES_PAGE_SIZE,
  type FleetOperatorNoteFilters,
  type UseFleetOperatorNotesOptions,
  type UseOperatorNotesResult,
} from "./operator-notes";
export type { FleetOperatorNoteSummary } from "../../client-messages/operator-notes";
export { parseTenantSignalFilters, type TenantSignalFilters } from "./signal-filters";
export {
  parseTenantActivationFilters,
  parseFleetActivationFilters,
  FLEET_DEFAULT_DAYS,
  type TenantActivationFilters,
  type FleetActivationFilters,
  type ParsedFleetActivationFilters,
  type ParsedTenantActivationFilters,
  type RawActivationFilters,
} from "./activation-filters";
export {
  useTenantIncidents,
  useFleetIncidents,
  useTenantIncident,
  useFleetIncident,
  useTenantIncidentSummary,
  useFleetIncidentSummary,
  INCIDENTS_PAGE_SIZE,
  type UseIncidentsResult,
  type UseFleetIncidentsOptions,
  type UseIncidentResult,
  type UseIncidentSummaryResult,
} from "./incidents";
export {
  parseIncidentFilters,
  INCIDENT_DEFAULT_DAYS,
  type IncidentFilters,
  type FleetIncidentFilters,
  type ParsedIncidentFilters,
} from "./incident-filters";
export { describeIncidentStep, type DescribedIncidentStep } from "./incident-steps";
export type {
  IncidentActivation,
  IncidentCall,
  IncidentDetail,
  IncidentPeriodSummary,
  IncidentStep,
  IncidentSummary,
  IncidentSummaryPeriod,
  IncidentSummaryResponse,
} from "../../client-messages/incidents";
export { isTimeZone } from "./wall-clock";
export { offsetLabel, timezoneAbbreviation } from "@visioni/a3s-dates";
export {
  defaultTimeZone,
  matchesTimeZone,
  timeZoneAfterSiteChange,
  timeZoneLabel,
  timeZoneOptions,
  type DefaultTimeZone,
  type TimeZoneGroup,
  type TimeZoneOption,
} from "./time-zone-choice";
export { activationCameraKey, chronological, formatElapsed } from "./activation-view";
export type { CursorPager } from "./cursor-pager";
export type {
  ActivationDetail,
  ActivationOperatorNote,
  ActivationSignal,
  ActivationSignalProcessing,
  ActivationSummary,
  FleetActivationDetail,
  FleetActivationSummary,
} from "../../client-messages/activations";
export type { SignalSummary } from "../../client-messages/signals";
export type { ListOrder } from "../../client-messages/activations";
export { useRunPatriotConnectionCheck } from "./connection-check";
export type {
  PatriotConnectionCheckStepResult,
  RunPatriotConnectionCheckResponse,
} from "../../client-messages/patriot-connection-check";
export {
  useStartPatriotBackfill,
  useControlPatriotBackfill,
  useBackfillRuns,
  BACKFILL_RUNS_PAGE_SIZE,
  type UseBackfillRunsOptions,
  type UseBackfillRunsResult,
} from "./backfill";
export {
  PATRIOT_BACKFILL_LANES,
  UNDATED_PATRIOT_BACKFILL_LANES,
  type PatriotBackfillControl,
  type PatriotBackfillLane,
  type PatriotBackfillRun,
  type PatriotBackfillScan,
  type PatriotBackfillScanName,
  type StartPatriotBackfillRequest,
  type StartPatriotBackfillResponse,
} from "../../client-messages/patriot-backfill";
export {
  useStartIncidentsRecompute,
  useIncidentsRecomputes,
  INCIDENTS_RECOMPUTES_PAGE_SIZE,
  type UseIncidentsRecomputesResult,
} from "./incidents-recompute";
export { incidentsRecomputePeriod } from "./incidents-recompute-period";
export type {
  IncidentsRecomputeChunk,
  IncidentsRecomputeRun,
  StartIncidentsRecomputeRequest,
  StartIncidentsRecomputeResponse,
} from "../../client-messages/incidents-recompute";
